import { useTranslation } from 'react-i18next';

import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Icon,
  Spinner,
  Text,
} from '@kognitos/lattice';

/**
 * Props for the AutomationVersionRow component.
 */
interface IAutomationVersionRowProps {
  /** Automation display name. */
  title: string;
  /** Currently selected revision id (e.g. "12.0"), or empty when none. */
  selectedRevisionId: string;
  /** Available revision version strings. */
  versions: string[];
  /** The subset of {@link versions} that are published (authoritative, derived
   * from each revision's snapshot `publish_time`). */
  publishedVersionIds: Set<string>;
  /** Whether the revisions are loading. */
  isLoading: boolean;
  /** Whether the revision list failed with nothing to show. The query does not
   * retry, so the failure is surfaced inline. Scoped to that case on purpose:
   * when earlier pages did load, the picker keeps its versions and "load more"
   * stays available to retry, so no error is shown. */
  isLoadError?: boolean;
  /** Whether more revisions can be loaded. */
  hasNextPage: boolean;
  /** Whether the next page is being fetched. */
  isFetchingNextPage: boolean;
  /** Called when the user picks a revision. */
  onSelect: (revisionId: string) => void;
  /** Called to load the next page of revisions. */
  onLoadMore: () => void;
  /** Called to retry after {@link isLoadError}. Required: the error copy
   * carries no recovery hint of its own, so omitting this leaves a dead end. */
  onRetry: () => void;
  /** The automation's overall draft state, used for the dot whenever the
   * selected revision's own stage is unknown: nothing selected yet, a failed
   * first page, or a selection outside the loaded pages. When the selection
   * *is* loaded, the dot follows that revision's snapshot `publish_time`
   * instead (see {@link publishedVersionIds}). */
  isDraft?: boolean;
}

/**
 * Bordered row showing an automation (status dot + name) and a version picker
 * dropdown (e.g. `v12.0`). Shared by the Export and Move dialogs.
 *
 * @param {IAutomationVersionRowProps} props - Component props
 * @returns {JSX.Element} The automation + version row
 */
export function AutomationVersionRow({
  title,
  selectedRevisionId,
  versions,
  publishedVersionIds,
  isLoading,
  isLoadError = false,
  hasNextPage,
  isFetchingNextPage,
  onSelect,
  onLoadMore,
  onRetry,
  isDraft = false,
}: IAutomationVersionRowProps) {
  const { t: tCommon } = useTranslation('common');
  const { t: tAutomation } = useTranslation('automation');

  // The dot reflects the selected revision's stage: a revision is published
  // when the server marked its snapshot with a publish_time (tracked in
  // publishedVersionIds). That is only knowable for revisions actually loaded,
  // so anything else — nothing selected yet, a failed first page, or a default
  // revision beyond the loaded pages — falls back to the automation's overall
  // draft state rather than claiming a stage. Note this stays correct after a
  // failed "load more": the pages already loaded remain authoritative.
  const isSelectionLoaded = versions.includes(selectedRevisionId);
  const selectedIsDraft = isSelectionLoaded
    ? !publishedVersionIds.has(selectedRevisionId)
    : isDraft;

  return (
    <Flex direction="column" gap={1} width="full">
      <Flex
        align="center"
        gap={2}
        width="full"
        className="rounded-lg border border-border py-2 pl-3 pr-2"
      >
        <Flex align="center" gap={2} className="min-w-0 flex-1">
          <span
            aria-hidden
            data-testid="automation-version-stage-dot"
            className={cn(
              'size-2 shrink-0 rounded-full',
              selectedIsDraft ? 'bg-informative' : 'bg-success',
            )}
          />
          <Text className="truncate">{title}</Text>
        </Flex>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              data-testid="automation-version-trigger"
              disabled={!isLoading && versions.length === 0}
            >
              {selectedRevisionId ? (
                <span>{`v${selectedRevisionId}`}</span>
              ) : isLoading ? (
                <Spinner size="sm" variant="muted" />
              ) : (
                <span>{tCommon('common.action.select')}</span>
              )}
              <Icon type="ChevronDown" size="sm" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
            {versions.map(version => (
              <DropdownMenuItem key={version} onClick={() => onSelect(version)}>
                {`v${version}`}
              </DropdownMenuItem>
            ))}
            {hasNextPage && (
              <DropdownMenuItem
                disabled={isFetchingNextPage}
                onSelect={event => {
                  event.preventDefault();
                  onLoadMore();
                }}
              >
                {isFetchingNextPage && <Spinner size="sm" variant="muted" />}
                {tCommon('common.action.loadMore')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </Flex>
      {isLoadError && (
        <Flex
          role="alert"
          align="center"
          gap={2}
          className="pl-1"
          data-testid="automation-version-error"
        >
          <Text level="xSmall" color="destructive">
            {tAutomation('automation.versionPicker.loadError')}
          </Text>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            data-testid="automation-version-retry"
            // Wrapped so the click event is not forwarded as an argument:
            // react-query reads the first parameter as RefetchOptions.
            onClick={() => onRetry()}
          >
            {tCommon('common.action.retry')}
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
